/**
 * Néerlandais des 16 landings thématiques.
 *
 * UNIQUEMENT du texte : pas de score, pas de couleur, pas de pourcentage,
 * pas de drapeau `weak`. Tout ce qui n'est pas une phrase vient du français
 * (`landings.js`) par superposition — cf. `landingTheme.js`. Un chiffre
 * n'est écrit qu'une fois, en français, et les trois langues l'affichent.
 *
 * Les tableaux sont fusionnés PAR INDICE : l'ordre des `checks`, `faqs`,
 * `kpis`, `args`, `letter` et `projection` doit rester celui du français.
 *
 * RÉFÉRENCES LÉGALES — la Belgique publie son droit dans les deux langues :
 * « AR » devient « KB » (koninklijk besluit), « Loi du … » devient « Wet van
 * … », « CCT n°109 » devient « CAO nr. 109 », « CDE » devient « WER »
 * (Wetboek van economisch recht), « ancien Code civil » devient « oud
 * Burgerlijk Wetboek ». Ce sont les intitulés officiels, pas des
 * traductions libres. Les dates et numéros d'article ne bougent pas.
 */
export const LANDING_THEMES_NL = {
  'pv': {
    badge: '🚗 Pv & snelheidsboetes',
    h1: ['Een pv gekregen?', 'Betaal niet voor je het weet.'],
    dropzone: 'Sleep je pv hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je pv',
    checksIntro: 'Eén van deze gebreken kan volstaan om het pv te doen vallen',
    checks: [
      ['Het ijkingscertificaat van de radar', '· vervallen, dan staat het pv zwak'],
      ['De signalisatie van de zone', '· afwezig of niet herhaald, dat is betwistbaar'],
      ['De toegepaste tolerantiemarge', '· ze is verplicht'],
      ['De verplichte vermeldingen van het proces-verbaal', '· datum, uur en plaats van de vaststelling, identiteit van de verbalisant'],
      ['De identificatie van de bestuurder', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op de Belgische verkeerswetgeving. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je pv zoals zij.',
    bandLead: 'De AI analyseert je pv in 7 passes.',
    faqTitle: 'Jouw vragen over pv’s',
    faqs: [
      { q: 'Moet ik meteen betalen?', a: 'Nee. En te snel betalen kan als aanvaarding gelden. Scan eerst: je weet dan of je pv betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je pv.' },
      { q: 'En als mijn pv onbetwistbaar is?', a: 'Archer zegt het je onomwonden. Snel betalen kan dan je beste optie zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je pv heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn pv analyseren · Gratis',
    proof: {
      kicker: '● PV BETWISTBAAR',
      headline: 'Drie bruikbare zwakke plekken.',
      summary: 'Je pv vertoont reële vormgebreken: het ijkingscertificaat van de radar wordt niet vermeld, de tolerantiemarge lijkt verkeerd toegepast, en de signalisatie van de zone is niet verantwoord.',
      kpis: [
        { label: 'Gevorderd bedrag', sub: 'boete + kosten' },
        { label: 'Als het pv sneuvelt', sub: 'je betaalt niets' },
        { label: 'Termijn om te betwisten', value: '12 dagen', sub: 'vanaf je document' },
      ],
      args: [
        { title: 'Het certificaat van jaarlijkse verificatie van de snelheidsmeter wordt niet voorgelegd', basis: 'KB van 12 oktober 2010 · homologatie en jaarlijkse controle', tag: 'STERK' },
        { title: 'De technische tolerantiemarge lijkt niet toegepast', basis: 'KB 12/10/2010 · de weerhouden snelheid moet worden gecorrigeerd', tag: 'STERK' },
        { title: 'De signalisatie van de zone wordt in het pv niet vastgesteld', basis: 'Art. 62 Wet van 16 maart 1968 · bewijskracht van het pv', tag: 'MATIG' },
      ],
      letter: [
        'De vraag naar het ijkingscertificaat van de radar',
        'De herberekening van de snelheid na tolerantiemarge',
        'De formele betwisting binnen de wettelijke termijn, aangetekend',
        'De vraag tot gemotiveerde seponering',
      ],
      cta: 'Mijn pv analyseren · Gratis',
      projection: [
        { label: 'Vernietiging' },
        { label: 'Vermindering' },
        { label: 'Behoud' },
      ],
      note: 'En als je pv stevig is, zegt Archer het je onomwonden: je betaalt snel, zonder extra kosten.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'huissier': {
    badge: '📨 Brief van de deurwaarder',
    h1: ['Schrijft een deurwaarder je aan?', 'Kijk na wat hij echt kan doen.'],
    dropzone: 'Sleep je brief hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je brief',
    checksIntro: 'Eén van deze gebreken kan volstaan om de schuldvordering te doen vallen',
    checks: [
      ['De aard van de brief', '· minnelijk of gerechtelijk, het verschil is enorm'],
      ['De gevorderde kosten', '· vaak opgeblazen, betwistbaar'],
      ['De verjaring van de schuld', '· een te oude schuld is niet meer opeisbaar'],
      ['De verplichte vermeldingen van de ingebrekestelling', '· identiteit van de schuldeiser, oorsprong en detail van de schuld'],
      ['De reële mogelijkheid tot beslag', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op de Belgische wetgeving inzake invordering. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je ingebrekestelling zoals zij.',
    bandLead: 'De AI analyseert je brief in 7 passes.',
    faqTitle: 'Jouw vragen over deurwaarders',
    faqs: [
      { q: 'Moet ik meteen betalen?', a: 'Nee. En te snel betalen kan als aanvaarding gelden. Scan eerst: je weet dan of de gevorderde schuld betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je ingebrekestelling.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Snel betalen kan dan je beste optie zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Deze brief heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn brief analyseren · Gratis',
    proof: {
      kicker: '● MINNELIJKE INGEBREKESTELLING',
      headline: 'Hij kan niets in beslag nemen. Nog niet.',
      summary: 'Deze brief is een minnelijke ingebrekestelling, geen gerechtelijke procedure. Geen enkel beslag is mogelijk zonder vonnis, en de gevorderde kosten overschrijden het wettelijk tarief.',
      kpis: [
        { label: 'Gevorderd bedrag', sub: 'waarvan invorderingskosten' },
        { label: 'Betwistbare kosten', sub: 'overschrijding van het tarief' },
        { label: 'Termijn om te reageren', value: '8 dagen', sub: 'vóór herinnering' },
      ],
      args: [
        { title: 'De invorderingskosten overschrijden het wettelijk plafond', basis: 'Wet van 20 december 2002 op de minnelijke invordering', tag: 'STERK' },
        { title: 'Geen enkel bewijs van de oorspronkelijke schuld is bijgevoegd', basis: 'Art. 1315 oud Burgerlijk Wetboek · bewijslast', tag: 'STERK' },
        { title: 'De verjaringstermijn zou bereikt kunnen zijn', basis: 'Art. 2262bis oud Burgerlijk Wetboek', tag: 'MATIG' },
      ],
      letter: [
        'De betwisting van de kosten boven het wettelijk tarief',
        'De vraag naar het bewijs van de oorspronkelijke schuld',
        'De herinnering dat geen beslag mogelijk is zonder vonnis',
        'De vraag naar een afbetalingsplan als de schuld verschuldigd is',
      ],
      cta: 'Mijn brief analyseren · Gratis',
      projection: [
        { label: 'Kosten verminderd' },
        { label: 'Schuld bevestigd' },
        { label: 'Zonder antwoord' },
      ],
      note: 'En als de schuld wel degelijk de jouwe is, zegt Archer het je: je weet wat je moet betalen, tegen de juiste prijs.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'caution': {
    badge: '🏠 Huurwaarborg',
    h1: ['Houdt je verhuurder je waarborg?', 'Misschien mag hij dat niet.'],
    dropzone: 'Sleep je document hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je waarborg',
    checksIntro: 'Eén van deze gebreken kan volstaan om de inhouding te doen vallen',
    checks: [
      ['Normale slijtage tegenover schade', '· de wettelijke grens is precies'],
      ['De plaatsbeschrijving bij uittrede', '· punt voor punt vergeleken met die bij intrede'],
      ['De bewijsstukken van de inhouding', '· facturen of bestekken verplicht'],
      ['De verplichte vermeldingen van de plaatsbeschrijving', '· tegensprekelijk opgesteld, gedateerd en door beide partijen ondertekend'],
      ['De evenredigheid van de inhouding', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op de Belgische huurwetgeving. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je plaatsbeschrijving zoals zij.',
    bandLead: 'De AI analyseert je dossier in 7 passes.',
    faqTitle: 'Jouw vragen over de huurwaarborg',
    faqs: [
      { q: 'Mogen ze mijn waarborg houden?', a: 'Alleen voor verantwoorde schade, met facturen of bestekken als staving, nooit voor normale slijtage. Scan eerst: je weet dan of de inhouding op je huurwaarborg betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik de inhouding betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je plaatsbeschrijving bij uittrede.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Afzien kan dan de juiste rekening zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je waarborg heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn waarborg analyseren · Gratis',
    proof: {
      kicker: '● INHOUDING BETWISTBAAR',
      headline: '1 850 € die jou toekomen.',
      summary: 'De vergelijking tussen de plaatsbeschrijving bij intrede en die bij uittrede toont normale slijtage op nagenoeg alle ingehouden punten. Geen enkele factuur verantwoordt het afgetrokken bedrag.',
      kpis: [
        { label: 'Ingehouden waarborg', sub: 'op 2 100 € gestort' },
        { label: 'Wat je recupereert', sub: 'geen enkel bewijsstuk' },
        { label: 'Termijn om te handelen', value: '2 maanden', sub: 'vóór verjaring' },
      ],
      args: [
        { title: 'De inhoudingen betreffen normale slijtage, niet aanrekenbaar', basis: 'Art. 1730 oud Burgerlijk Wetboek · verplichtingen bij uittrede', tag: 'STERK' },
        { title: 'Geen enkele factuur of bestek verantwoordt het ingehouden bedrag', basis: 'Wet van 20 februari 1991, art. 10 · schriftelijk bewijs', tag: 'STERK' },
        { title: 'De termijn voor teruggave van het saldo is overschreden', basis: 'Vaste rechtspraak van de vredegerechten', tag: 'MATIG' },
      ],
      letter: [
        'De vraag tot integrale teruggave binnen 15 dagen',
        'De punt-voor-punt betwisting van de plaatsbeschrijving',
        'De vraag naar de ontbrekende bewijsstukken',
        'De herinnering aan de intresten op de geblokkeerde rekening',
      ],
      cta: 'Mijn waarborg analyseren · Gratis',
      projection: [
        { label: 'Volledige teruggave' },
        { label: 'Gedeeltelijke teruggave' },
        { label: 'Weigering' },
      ],
      note: 'En als de inhoudingen verantwoord zijn, zegt Archer het je duidelijk: je weet precies waarom.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'licenciement': {
    badge: '💼 Ontslag & beëindiging',
    h1: ['Net ontslagen?', 'Kijk na wat men je verschuldigd is voor je tekent.'],
    dropzone: 'Sleep je brief of je C4 hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je ontslag',
    checksIntro: 'Eén van deze punten kan meerdere duizenden euro’s waard zijn',
    checks: [
      ['De gerespecteerde opzegtermijn', '· de wettelijke duur volgens je anciënniteit'],
      ['De compenserende vergoeding', '· de exacte berekening als de opzeg niet gepresteerd wordt'],
      ['De ingeroepen reden', '· een kennelijk onredelijk ontslag geeft recht op vergoeding'],
      ['De verplichte vermeldingen van de opzeggingsbrief', '· begin en duur van de opzeg, wettelijke vorm nageleefd'],
      ['De naleving van de procedure CAO 109', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op het Belgisch arbeidsrecht. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je C4 zoals zij.',
    bandLead: 'De AI analyseert je dossier in 7 passes.',
    faqTitle: 'Jouw vragen over ontslag',
    faqs: [
      { q: 'Waar heb ik recht op?', a: 'Op de opzeg of de compenserende opzeggingsvergoeding, op het vertrekvakantiegeld, en op de motivering van je ontslag als je erom vraagt. Scan eerst: je weet dan of je ontslagbrief betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik dat opeisen?', a: 'Ja, dat is een recht. De vordering volgt een precieze procedure: Archer geeft ze je, afgestemd op je C4.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Afzien kan dan de juiste rekening zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou opeist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je dossier heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn C4 analyseren · Gratis',
    proof: {
      kicker: '● VERGOEDINGEN VERSCHULDIGD',
      headline: 'Hij is je nog 2 400 € verschuldigd.',
      summary: 'Je opzegtermijn van 3 maanden werd volgens je loonfiches slechts gedeeltelijk gepresteerd. Het saldo duikt nergens op. Ook het vertrekvakantiegeld werd niet uitbetaald.',
      kpis: [
        { label: 'Uitbetaald saldo', sub: 'tot op vandaag' },
        { label: 'Wat hij je verschuldigd is', sub: 'volledige berekening' },
        { label: 'Termijn om te handelen', value: '34 dagen', sub: 'vóór verjaring' },
      ],
      args: [
        { title: 'Onvolledige opzeg · 6 weken niet gedekt', basis: 'Wet van 3 juli 1978, art. 37 · berekening volgens de anciënniteit', tag: 'STERK' },
        { title: 'Vertrekvakantiegeld niet uitbetaald', basis: 'KB van 30 maart 1967 · verplichte betaling bij einde contract', tag: 'STERK' },
        { title: 'Geen formele motivering van het ontslag', basis: 'CAO nr. 109 · recht op motivering', tag: 'MATIG' },
      ],
      letter: [
        'De becijferde vraag naar het ontbrekende saldo van de opzeg',
        'De vraag naar het vertrekvakantiegeld',
        'De vraag naar de formele motivering van het ontslag',
        'De antwoordtermijn en het gevolg bij stilzwijgen',
      ],
      cta: 'Mijn C4 analyseren · Gratis',
      projection: [
        { label: 'Volledig verkregen' },
        { label: 'Gedeeltelijk verkregen' },
        { label: 'Weigering' },
      ],
      note: 'En als je eindafrekening correct is, bevestigt Archer het je, geen nutteloze vordering.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'facture': {
    badge: '🧾 Betwiste factuur',
    h1: ['Een factuur die niet klopt?', 'Betaal niet voor je ze hebt nagekeken.'],
    dropzone: 'Sleep je factuur hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je factuur',
    checksIntro: 'Eén van deze gebreken kan volstaan om de factuur te doen vallen',
    checks: [
      ['De verborgen kosten', '· activering, opzegging, niet-gevraagde diensten'],
      ['De samenhang met je contract', '· stemt de aangerekende prijs overeen met wat je tekende'],
      ['De niet-toegestane SEPA-domiciliëringen', '· automatische terugbetaling binnen 8 weken'],
      ['De verplichte vermeldingen van de factuur', '· identiteit van de dienstverlener, detail van de prestaties, btw'],
      ['De toegepaste indexering', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op het Belgisch Wetboek van economisch recht. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je factuur zoals zij.',
    bandLead: 'De AI analyseert je factuur in 7 passes.',
    faqTitle: 'Jouw vragen over facturen',
    faqs: [
      { q: 'Moet ik meteen betalen?', a: 'Nee. En te snel betalen kan als aanvaarding gelden. Scan eerst: je weet dan of je factuur betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je factuur.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Snel betalen kan dan je beste optie zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je factuur heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn factuur analyseren · Gratis',
    proof: {
      kicker: '● FACTUUR BETWISTBAAR',
      headline: '247 € onverantwoorde kosten.',
      summary: 'Je operator rekent opzeggingskosten aan terwijl je oorspronkelijk contract van 24 maanden al 3 maanden verstreken is: de opzegging had gratis moeten zijn.',
      kpis: [
        { label: 'Gevorderd bedrag', sub: 'opzeggingskosten' },
        { label: 'Als de factuur sneuvelt', sub: 'je betaalt niets' },
        { label: 'Termijn om te betwisten', value: '30 dagen', sub: 'vanaf je document' },
      ],
      args: [
        { title: 'Oorspronkelijk contract 3 maanden verstreken · gratis opzegging verschuldigd', basis: 'Art. 111/3 §3, Wet van 13 juni 2005', tag: 'STERK' },
        { title: 'Opzeggingskosten niet gedetailleerd op de factuur', basis: 'Art. VI.83 WER · onrechtmatige bedingen', tag: 'STERK' },
        { title: 'Geen ondertekend akkoord voor de stilzwijgende verlenging', basis: 'Art. VI.45 WER · voorafgaande informatie', tag: 'MATIG' },
      ],
      letter: [
        'De formele betwisting van de opzeggingskosten',
        'De vraag naar de gedetailleerde verantwoording van het bedrag',
        'De vraag tot terugbetaling binnen 30 dagen',
        'De ingebrekestelling bij stilzwijgen',
      ],
      cta: 'Mijn factuur analyseren · Gratis',
      projection: [
        { label: 'Volledige terugbetaling' },
        { label: 'Gedeeltelijke terugbetaling' },
        { label: 'Weigering' },
      ],
      note: 'En als de kosten verantwoord zijn, zegt Archer het je duidelijk: je weet precies waarvoor je betaalt.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'arnaque': {
    badge: '🛒 Online aankoop',
    h1: ['Nooit geleverd, nooit terugbetaald?', 'Je hebt meer rechten dan je denkt.'],
    dropzone: 'Sleep je aankoopbewijs hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je bestelling',
    checksIntro: 'Eén van deze punten kan volstaan om je terugbetaling te krijgen',
    checks: [
      ['De wettelijke leveringstermijn', '· maximaal 30 dagen, daarna kan je terugbetaling eisen'],
      ['Het herroepingsrecht', '· 14 dagen, ook zonder reden'],
      ['De wettelijke garantie van conformiteit', '· 2 jaar, ook als de verkoper er niet over rept'],
      ['De verplichte vermeldingen van de orderbevestiging', '· bevestiging van het contract op duurzame drager, totale prijs'],
      ['De verifieerbare identiteit van de verkoper', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op het Belgisch Wetboek van economisch recht. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je bestelling zoals zij.',
    bandLead: 'De AI analyseert je dossier in 7 passes.',
    faqTitle: 'Jouw vragen over online oplichting',
    faqs: [
      { q: 'Kan ik terugbetaald worden?', a: 'Ja als de verkoper niet binnen 30 dagen geleverd heeft, en sowieso binnen de 14 dagen herroeping, ook zonder reden. Scan eerst: je weet dan of je bestelling recht geeft op terugbetaling en hoeveel tijd je hebt.' },
      { q: 'Mag ik de terugbetaling eisen?', a: 'Ja, dat is een recht. De vordering volgt een precieze procedure: Archer geeft ze je, afgestemd op je bestelling en je aankoopbewijs.' },
      { q: 'En als de verkoper verdwenen is?', a: 'Archer zegt het je onomwonden. De bankbetwisting hangt niet van hem af, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou opeist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je dossier heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn bestelling analyseren · Gratis',
    proof: {
      kicker: '● TERUGBETALING VERSCHULDIGD',
      headline: 'De verkoper zit fout, niet jij.',
      summary: 'Je bestelling moest binnen 30 dagen geleverd worden: er zijn 52 dagen verstreken zonder nieuws. Het recht op ontbinding van het contract staat open, met integrale terugbetaling.',
      kpis: [
        { label: 'Gevorderd bedrag', sub: 'niet-geleverde bestelling' },
        { label: 'Wat je recupereert', sub: 'integrale terugbetaling' },
        { label: 'Termijn om te handelen', value: 'Onmiddellijk', sub: 'wettelijke termijn overschreden' },
      ],
      args: [
        { title: 'Levering niet uitgevoerd na 52 dagen', basis: 'Art. VI.43 WER · leveringsplicht binnen 30 dagen', tag: 'STERK' },
        { title: 'Geen antwoord van de verkoper op de herinneringen', basis: 'Art. VI.44 WER · recht op ontbinding van het contract', tag: 'STERK' },
        { title: 'Geen verifieerbare wettelijke vermeldingen op de site', basis: 'Art. VI.45 WER · informatieplicht', tag: 'MATIG' },
      ],
      letter: [
        'De ingebrekestelling om binnen 8 dagen te leveren',
        'De vraag tot ontbinding en integrale terugbetaling',
        'De verwijzing naar de toepasselijke artikelen van het WER',
        'De procedure van bankbetwisting bij stilzwijgen',
      ],
      cta: 'Mijn bestelling analyseren · Gratis',
      projection: [
        { label: 'Terugbetaling verkregen' },
        { label: 'Gedeeltelijke terugbetaling' },
        { label: 'Zonder antwoord' },
      ],
      note: 'En als je bestelling nog binnen de termijn valt, zegt Archer het je, geen nutteloze stap.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'bail': {
    badge: '🏠 Huur & woning',
    h1: ['Zet je verhuurder je buiten?', 'Kijk na of hij dat mag.'],
    dropzone: 'Sleep je brief hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je huurcontract',
    checksIntro: 'Eén van deze gebreken kan volstaan om de opzegging te doen vallen',
    checks: [
      ['De opzegtermijn', '· wettelijke duur en toegelaten redenen volgens het gewest'],
      ['De vorm van de opzegging', '· aangetekend verplicht, anders nietig'],
      ['De huurverhoging', '· correcte indexering, zonder onrechtmatige bedingen'],
      ['De verplichte vermeldingen van het schriftelijk huurcontract', '· huurprijs, duur, aanduiding van het goed en registratie'],
      ['De plaatsbeschrijving: slijtage tegenover schade', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op de gewestelijke huurwetgeving. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je huurcontract zoals zij.',
    bandLead: 'De AI analyseert je huurcontract in 7 passes.',
    faqTitle: 'Jouw vragen over het huurcontract',
    faqs: [
      { q: 'Moet ik meteen vertrekken?', a: 'Nee. En te snel vertrekken kan als aanvaarding van de opzegging gelden. Scan eerst: je weet dan of de ontvangen opzegging betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je opzegging en je huurcontract.' },
      { q: 'En als de opzegging volkomen geldig is?', a: 'Archer zegt het je onomwonden. Je weet dan tegen welke datum je echt moet vertrekken, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je opzegging heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn opzegging analyseren · Gratis',
    proof: {
      kicker: '● OPZEGGING ONREGELMATIG',
      headline: 'De opzeg leeft de wettelijke vorm niet na.',
      summary: 'De opzegging van je verhuurder vermeldt niet de reden die voor een vervroegde opzegging vereist is, en de betekende termijn is korter dan het wettelijk minimum voor jouw huurcontract.',
      kpis: [
        { label: 'Betekende termijn', value: '60 dagen', sub: 'geëist door de brief' },
        { label: 'Werkelijk verschuldigde termijn', value: '6 maanden', sub: 'wettelijk minimum' },
        { label: 'Termijn om te reageren', value: '15 dagen', sub: 'vóór uitwerking' },
      ],
      args: [
        { title: 'Reden voor vervroegde opzegging niet verantwoord in de brief', basis: 'Woninghuurwet, art. 3 §5 · omkaderde redenen', tag: 'STERK' },
        { title: 'Opzegtermijn korter dan het wettelijk minimum', basis: 'Art. 3 §2 · minimumduur volgens de reden', tag: 'STERK' },
        { title: 'Geen aangetekende verzending', basis: 'Art. 3 §1 · verplichte vorm, anders nietigheid', tag: 'MATIG' },
      ],
      letter: [
        'De formele betwisting van de onregelmatige opzeg',
        'De vraag tot naleving van de wettelijke minimumtermijn',
        'De herinnering aan de nietigheid bij vormgebrek',
        'De volgende stappen bij onenigheid',
      ],
      cta: 'Mijn opzegging analyseren · Gratis',
      projection: [
        { label: 'Opzegging vernietigd' },
        { label: 'Termijn heronderhandeld' },
        { label: 'Opzegging behouden' },
      ],
      note: 'En als de opzegging volkomen geldig is, zegt Archer het je, je organiseert je met een gerust hoofd.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'assurance': {
    badge: '🛡️ Verzekeringsgeschil',
    h1: ['Weigert je verzekeraar te betalen?', 'Laat zijn weigering nakijken.'],
    dropzone: 'Sleep de weigeringsbrief hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je weigering',
    checksIntro: 'Eén van deze gebreken kan volstaan om de weigering te doen vallen',
    checks: [
      ['De motivering van de weigering', '· moet precies zijn, niet louter een geciteerd beding'],
      ['Het ingeroepen beding', '· de geldigheid en de werkelijke toepassing ervan'],
      ['De behandelingstermijn', '· een verzekeraar die talmt kan in gebreke worden gesteld'],
      ['De verplichte vermeldingen van het contract', '· een uitsluiting die niet in de polis staat is niet tegenwerpbaar'],
      ['Het voorgestelde bedrag tegenover de werkelijke waarde', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op de Belgische verzekeringswet. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je weigeringsbrief zoals zij.',
    bandLead: 'De AI analyseert je dossier in 7 passes.',
    faqTitle: 'Jouw vragen over verzekeringsgeschillen',
    faqs: [
      { q: 'Mogen ze weigeren?', a: 'Alleen op grond van een in het contract voorziene uitsluiting, en met een precieze motivering: een geciteerd beding volstaat niet. Scan eerst: je weet dan of de weigeringsbrief betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je weigeringsbrief.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Afzien kan dan de juiste rekening zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je beroep heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn weigering analyseren · Gratis',
    proof: {
      kicker: '● WEIGERING BETWISTBAAR',
      headline: 'Het ingeroepen beding is niet van toepassing.',
      summary: 'De verzekeraar weigert je schadegeval met een beroep op een uitsluiting wegens gebrek aan onderhoud, maar geen enkel expertiseverslag staaft dat. Deze weigering steunt op een bewering, niet op een bewijs.',
      kpis: [
        { label: 'Bedrag van het schadegeval', sub: 'geraamde waarde' },
        { label: 'Wat je recupereert', sub: 'als het lukt' },
        { label: 'Termijn om te betwisten', value: '1 maand', sub: 'vóór verjaring' },
      ],
      args: [
        { title: 'Weigering niet gemotiveerd door een gedocumenteerde expertise', basis: 'Wet van 4 april 2014, art. 62 · motiveringsplicht', tag: 'STERK' },
        { title: 'Geen bewijs van het ingeroepen gebrek aan onderhoud', basis: 'Art. 1315 oud Burgerlijk Wetboek · bewijslast', tag: 'STERK' },
        { title: 'Behandelingstermijn van het dossier overschreden', basis: 'Art. 22 Wet van 4 april 2014', tag: 'MATIG' },
      ],
      letter: [
        'De betwisting van de weigering en de vraag naar het expertiseverslag',
        'De vraag naar een onafhankelijke tegenexpertise',
        'De ingebrekestelling om het dossier opnieuw te onderzoeken',
        'De herinnering aan de beroepswegen (ombudsman van de verzekeringen)',
      ],
      cta: 'Mijn weigering analyseren · Gratis',
      projection: [
        { label: 'Vergoeding verkregen' },
        { label: 'Gedeeltelijke vergoeding' },
        { label: 'Weigering behouden' },
      ],
      note: 'En als de weigering gegrond is, legt Archer het je duidelijk uit, zodat je weet waar je aan toe bent.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'telecom': {
    badge: '📱 Telecom · Proximus, Orange, Telenet',
    h1: ['Is je telecomfactuur opgelopen?', 'Ze rekenen erop dat je niet leest.'],
    dropzone: 'Sleep je factuur hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je telecomfactuur',
    checksIntro: 'Eén van deze gebreken kan volstaan om de kosten te doen vallen',
    checks: [
      ['De opzeggingskosten', '· vaak aangerekend terwijl de verbintenis afgelopen is'],
      ['De nooit onderschreven opties', '· opgedoken zonder ondertekend akkoord'],
      ['De eenzijdige prijsverhoging', '· je hebt een recht op kosteloze opzegging'],
      ['De verplichte vermeldingen van de telecomfactuur', '· detail van de posten, tarieven en bijkomende kosten'],
      ['De samenhang met je oorspronkelijk contract', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op het Belgisch Wetboek van economisch recht en de telecomwet. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je telecomfactuur zoals zij.',
    bandLead: 'De AI analyseert je factuur in 7 passes.',
    faqTitle: 'Jouw vragen over telecomgeschillen',
    faqs: [
      { q: 'Moet ik meteen betalen?', a: 'Nee. En te snel betalen kan als aanvaarding gelden. Scan eerst: je weet dan of je telecomfactuur betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je telecomfactuur.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Snel betalen kan dan je beste optie zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je factuur heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn factuur analyseren · Gratis',
    proof: {
      kicker: '● KOSTEN BETWISTBAAR',
      headline: '247 € die je operator je verschuldigd is.',
      summary: 'Er worden je opzeggingskosten aangerekend terwijl je verbintenisperiode al 3 maanden afgelopen is, en er duiken opties op je factuur op zonder dat enig ondertekend akkoord ze verantwoordt.',
      kpis: [
        { label: 'Gevorderd bedrag', sub: 'kosten + opties' },
        { label: 'Wat je recupereert', sub: 'als de betwisting lukt' },
        { label: 'Termijn om te betwisten', value: '30 dagen', sub: 'vanaf de factuur' },
      ],
      args: [
        { title: 'Opzeggingskosten aangerekend na het einde van de verbintenis', basis: 'Art. 111/3 §3, Wet van 13 juni 2005 · kosteloosheid na de minimumperiode', tag: 'STERK' },
        { title: 'Opties aangerekend zonder bewijs van onderschrijving', basis: 'Art. VI.45 WER · informatieplicht en voorafgaand akkoord', tag: 'STERK' },
        { title: 'Verlengingsbeding niet ter kennis gebracht', basis: 'Art. VI.83 WER · onrechtmatige bedingen', tag: 'MATIG' },
      ],
      letter: [
        'De gedetailleerde betwisting post per post',
        'De vraag naar het bewijs van onderschrijving van de opties',
        'De vraag tot terugbetaling binnen 30 dagen',
        'De aanhangigmaking bij de Ombudsdienst voor Telecommunicatie bij weigering',
      ],
      cta: 'Mijn telecomfactuur analyseren · Gratis',
      projection: [
        { label: 'Volledige terugbetaling' },
        { label: 'Commercieel gebaar' },
        { label: 'Weigering' },
      ],
      note: 'En als de kosten verschuldigd zijn, zegt Archer het je duidelijk: je betaalt wat juist is, niets meer.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'energie': {
    badge: '⚡ Energie · afrekening',
    h1: ['1 480 € afrekening?', 'Kijk na voor je betaalt.'],
    dropzone: 'Sleep je factuur hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je energiefactuur',
    checksIntro: 'Eén van deze gebreken kan volstaan om een deel van het bedrag te doen vallen',
    checks: [
      ['Geschatte tegenover opgenomen meterstanden', '· een factuur moet je werkelijke verbruik weergeven'],
      ['De gefactureerde periode', '· boven 5 jaar is het verbruik verjaard'],
      ['Het toegepaste tarief', '· conform je contract of eenzijdig gewijzigd'],
      ['De verplichte vermeldingen van de energiefactuur', '· leveringspunt, exacte periode en toegepast tarief'],
      ['De toegevoegde bijkomende kosten', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op het Belgisch consumenten- en energierecht. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je energiefactuur zoals zij.',
    bandLead: 'De AI analyseert je factuur in 7 passes.',
    faqTitle: 'Jouw vragen over energiefacturen',
    faqs: [
      { q: 'Moet ik meteen betalen?', a: 'Nee. En te snel betalen kan als aanvaarding gelden. Scan eerst: je weet dan of je afrekeningsfactuur betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je energiefactuur.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Snel betalen kan dan je beste optie zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je afrekening heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn factuur analyseren · Gratis',
    proof: {
      kicker: '● AFREKENING BETWISTBAAR',
      headline: '1 480 € om van dichtbij na te kijken.',
      summary: 'Je jaarlijkse afrekening steunt op geschatte meterstanden, niet op opgenomen standen. Een deel van de gefactureerde periode valt buiten de termijn waarbinnen een leverancier verbruik kan vorderen.',
      kpis: [
        { label: 'Gevorderd bedrag', sub: 'jaarlijkse afrekening' },
        { label: 'Mogelijk vernietigbaar', sub: 'verjaarde periode' },
        { label: 'Termijn om te betwisten', value: '30 dagen', sub: 'vanaf de factuur' },
      ],
      args: [
        { title: 'Facturatie op basis van geschatte, nooit opgenomen meterstanden', basis: 'Verplichting tot facturatie op werkelijk verbruik', tag: 'STERK' },
        { title: 'Een deel van de gevorderde periode is verjaard', basis: 'Art. 2277 oud Burgerlijk Wetboek · vijfjarige verjaring van verbruiken', tag: 'STERK' },
        { title: 'Geen afbetalingsplan voorgesteld vóór ingebrekestelling', basis: 'Omkaderde praktijk van de energiesector', tag: 'MATIG' },
      ],
      letter: [
        'De vraag naar het detail van de opgenomen tegenover de geschatte standen',
        'De betwisting van de verjaarde periode',
        'De vraag naar een gespreid afbetalingsplan',
        'De aanhangigmaking bij de ombudsman voor energie bij blokkering',
      ],
      cta: 'Mijn energiefactuur analyseren · Gratis',
      projection: [
        { label: 'Factuur gecorrigeerd' },
        { label: 'Afbetalingsplan' },
        { label: 'Behoud' },
      ],
      note: 'En als het verbruik reëel is, zegt Archer het je, en helpt het je een spreiding te krijgen in plaats van een ingebrekestelling.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'sepa': {
    badge: '🏦 Niet-toegestane SEPA-domiciliëringen',
    h1: ['Een afhouding die je niet herkent?', 'Je kan terugbetaling eisen.'],
    dropzone: 'Sleep je rekeninguittreksel hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je domiciliëringen',
    checksIntro: 'Eén van deze punten kan volstaan om je geld terug te krijgen',
    checks: [
      ['Het bestaan van een ondertekend mandaat', '· zonder mandaat is de domiciliëring niet toegestaan'],
      ['Het venster van 8 weken', '· terugbetaling zonder verantwoording, op eenvoudig verzoek'],
      ['De niet-toegestane domiciliëringen', '· terugvorderbaar tot 13 maanden'],
      ['De verplichte vermeldingen van het domiciliëringsmandaat', '· unieke referentie, identificatie van de schuldeiser, datum en handtekening'],
      ['De werkelijke identiteit van de schuldeiser', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op het Belgisch en Europees betalingsdienstenrecht. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je uittreksel zoals zij.',
    bandLead: 'De AI analyseert je uittreksel in 7 passes.',
    faqTitle: 'Jouw vragen over domiciliëringen',
    faqs: [
      { q: 'Kan ik mijn geld recupereren?', a: 'Ja, zonder je te moeten verantwoorden binnen 8 weken, en tot 13 maanden als de domiciliëring niet toegestaan was. Scan eerst: je weet dan of je domiciliëringen betwistbaar zijn en hoeveel tijd je hebt.' },
      { q: 'Mag ik de terugbetaling vragen?', a: 'Ja, dat is een recht. De vraag volgt een precieze procedure: Archer geeft ze je, afgestemd op je uittreksel en het domiciliëringsmandaat.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Afzien kan dan de juiste rekening zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou opeist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je domiciliëringen hebben termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn domiciliëringen analyseren · Gratis',
    proof: {
      kicker: '● TERUGBETALING VAN RECHTSWEGE',
      headline: 'Je bank moet je terugbetalen.',
      summary: 'Er duiken SEPA-domiciliëringen op je rekening op zonder terugvindbaar ondertekend mandaat. Voor een toegestane SEPA-domiciliëring beschik je over 8 weken om zonder verantwoording terugbetaling te vragen.',
      kpis: [
        { label: 'Afgehouden bedrag', sub: 'over 12 maanden' },
        { label: 'Onmiddellijk recupereerbaar', sub: 'binnen het venster van 8 weken' },
        { label: 'Na te leven termijn', value: '8 weken', sub: 'per domiciliëring' },
      ],
      args: [
        { title: 'Geen enkel ondertekend domiciliëringsmandaat kan worden voorgelegd', basis: 'Verordening (EU) 260/2012 · het mandaat is verplicht', tag: 'STERK' },
        { title: 'Recht op terugbetaling zonder verantwoording binnen 8 weken', basis: 'Boek VII WER · betalingsdiensten', tag: 'STERK' },
        { title: 'Niet-toegestane domiciliëringen: terugbetaling mogelijk tot 13 maanden', basis: 'Boek VII WER · niet-toegestane verrichtingen', tag: 'MATIG' },
      ],
      letter: [
        'De vraag tot onmiddellijke terugbetaling bij je bank',
        'De betwisting van de domiciliëringen bij de schuldeiser',
        'De vraag tot voorlegging van het ondertekende SEPA-mandaat',
        'De formele herroeping van het mandaat voor de toekomst',
      ],
      cta: 'Mijn domiciliëringen analyseren · Gratis',
      projection: [
        { label: 'Terugbetaling verkregen' },
        { label: 'Gedeeltelijke terugbetaling' },
        { label: 'Geschil voort te zetten' },
      ],
      note: 'En als het mandaat wel bestaat, zegt Archer het je, en legt het uit hoe je het netjes herroept.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'vitesse': {
    badge: '📷 Snelheidsovertreding · radar, lidar, zone 30',
    h1: ['Geflitst?', 'Betaal niet voor je het weet.'],
    dropzone: 'Sleep je pv hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je snelheids-pv',
    checksIntro: 'Eén van deze gebreken kan volstaan om het pv te doen vallen',
    checks: [
      ['Het ijkingscertificaat van het toestel', '· vervallen, dan staat het pv zwak'],
      ['De signalisatie van de zone', '· afwezig of niet herhaald, dat is betwistbaar, en vaak vergeten'],
      ['De toegepaste tolerantiemarge', '· afgetrokken van de meting, ze is verplicht'],
      ['De verplichte vermeldingen van het proces-verbaal', '· gebruikt toestel, gemeten snelheid en weerhouden snelheid'],
      ['De verzendtermijn van het afschrift van het pv', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op de Belgische verkeerswetgeving. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je pv zoals zij.',
    bandLead: 'De AI analyseert je pv in 7 passes.',
    faqTitle: 'Jouw vragen over snelheids-pv’s',
    faqs: [
      { q: 'Moet ik meteen betalen?', a: 'Nee. En te snel betalen kan als aanvaarding gelden. Scan eerst: je weet dan of je pv betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je pv.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Snel betalen kan dan je beste optie zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je pv heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn pv analyseren · Gratis',
    proof: {
      kicker: '● PV BETWISTBAAR',
      headline: 'Drie bruikbare zwakke plekken.',
      summary: 'Je pv vertoont reële vormgebreken: het ijkingscertificaat van de radar wordt niet vermeld, de tolerantiemarge lijkt verkeerd toegepast, en de signalisatie van de zone is niet verantwoord.',
      kpis: [
        { label: 'Gevorderd bedrag', sub: 'boete + kosten' },
        { label: 'Als het pv sneuvelt', sub: 'je betaalt niets' },
        { label: 'Termijn om te betwisten', value: '12 dagen', sub: 'vanaf je document' },
      ],
      args: [
        { title: 'Het certificaat van jaarlijkse verificatie van de snelheidsmeter wordt niet voorgelegd', basis: 'KB van 12 oktober 2010 · homologatie en jaarlijkse controle', tag: 'STERK' },
        { title: 'De technische tolerantiemarge lijkt niet toegepast', basis: 'KB 12/10/2010 · de weerhouden snelheid moet worden gecorrigeerd', tag: 'STERK' },
        { title: 'De signalisatie van de zone wordt in het pv niet vastgesteld', basis: 'Art. 62 Wet van 16 maart 1968 · bewijskracht van het pv', tag: 'MATIG' },
      ],
      letter: [
        'De vraag naar het ijkingscertificaat van de radar',
        'De herberekening van de snelheid na tolerantiemarge',
        'De formele betwisting binnen de wettelijke termijn, aangetekend',
        'De vraag tot gemotiveerde seponering',
      ],
      cta: 'Mijn pv analyseren · Gratis',
      projection: [
        { label: 'Vernietiging' },
        { label: 'Vermindering' },
        { label: 'Behoud' },
      ],
      note: 'En als je pv stevig is, zegt Archer het je onomwonden: je betaalt snel, zonder extra kosten.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'telephone-volant': {
    badge: '📵 Gsm achter het stuur',
    h1: ['Geverbaliseerd voor je gsm?', 'Kijk na wat het pv echt bewijst.'],
    dropzone: 'Sleep je pv hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je pv',
    checksIntro: 'Eén van deze gebreken kan volstaan om het pv te doen vallen',
    checks: [
      ['De precieze beschrijving van het vastgestelde gebruik', '· vasthouden ≠ telefoneren: het onderscheid is juridisch'],
      ['De bijgevoegde materiële elementen', '· foto, video, of een loutere bewering?'],
      ['De toestand van het voertuig', '· stilstaand op een toegelaten plaats, dan valt de overtreding weg'],
      ['De verplichte vermeldingen van het proces-verbaal', '· precieze beschrijving van de vastgestelde feiten, datum en plaats'],
      ['De weerhouden graad van de overtreding', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op de Belgische Wegcode. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je pv zoals zij.',
    bandLead: 'De AI analyseert je pv in 7 passes.',
    faqTitle: 'Jouw vragen over de gsm achter het stuur',
    faqs: [
      { q: 'Moet ik meteen betalen?', a: 'Nee. En te snel betalen kan als aanvaarding gelden. Scan eerst: je weet dan of je pv betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je pv.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Snel betalen kan dan je beste optie zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je pv heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn pv analyseren · Gratis',
    proof: {
      kicker: '● VASTSTELLING BETWISTBAAR',
      headline: 'Vasthouden is niet telefoneren.',
      summary: 'Het proces-verbaal beweert dat de gsm gebruikt werd zonder precies te beschrijven wat werd vastgesteld. De overtreding veronderstelt echter het effectieve gebruik van het in de hand gehouden toestel, niet de loutere aanwezigheid ervan.',
      kpis: [
        { label: 'Gevorderd bedrag', sub: 'boete van 2e graad' },
        { label: 'Als het pv sneuvelt', sub: 'je betaalt niets' },
        { label: 'Termijn om te betwisten', value: '15 dagen', sub: 'vanaf de kennisgeving' },
      ],
      args: [
        { title: 'Het pv beschrijft het vastgestelde gebruik niet precies', basis: 'Art. 8.4 Wegcode · gebruik van het in de hand gehouden toestel', tag: 'STERK' },
        { title: 'Geen foto of materieel element staaft de vaststelling', basis: 'Art. 62 Wet van 16 maart 1968 · bewijskracht beperkt tot de vaststellingen', tag: 'STERK' },
        { title: 'Het voertuig kon stilstaan op een toegelaten plaats', basis: 'Art. 8.4 · de overtreding veronderstelt het besturen', tag: 'MATIG' },
      ],
      letter: [
        'De vraag naar het volledige proces-verbaal en de bijlagen',
        'De betwisting van de materialiteit van de vastgestelde feiten',
        'De vraag om toegang tot eventuele beelden',
        'De vraag tot gemotiveerde seponering',
      ],
      cta: 'Mijn pv analyseren · Gratis',
      projection: [
        { label: 'Vernietiging' },
        { label: 'Vermindering' },
        { label: 'Behoud' },
      ],
      note: 'En als de vaststelling stevig is, zegt Archer het je: je betaalt snel, zonder bijkomende kosten.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'jamais-livre': {
    badge: '📦 Nooit geleverd, nooit terugbetaald',
    h1: ['Vinted, valse site, spookverkoper?', 'Je hebt meer rechten dan je denkt.'],
    dropzone: 'Sleep je aankoopbewijs hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je bestelling',
    checksIntro: 'Eén van deze punten kan volstaan om je terugbetaling te krijgen',
    checks: [
      ['De wettelijke leveringstermijn', '· maximaal 30 dagen, daarna kan je terugbetaling eisen'],
      ['Het herroepingsrecht', '· 14 dagen, ook zonder reden'],
      ['De wettelijke garantie van conformiteit', '· 2 jaar, ook als de verkoper er niet over rept'],
      ['De verplichte vermeldingen van de onlineverkoper', '· identiteit, adres en ondernemingsnummer verifieerbaar'],
      ['De betaalwijze en je bancaire verhaalmogelijkheden', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op het Belgisch Wetboek van economisch recht. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je bestelling zoals zij.',
    bandLead: 'De AI analyseert je dossier in 7 passes.',
    faqTitle: 'Jouw vragen over niet-geleverde aankopen',
    faqs: [
      { q: 'Kan ik annuleren en terugbetaald worden?', a: 'Ja: na 30 dagen zonder levering kan je de ontbinding van de bestelling en de integrale terugbetaling eisen. Scan eerst: je weet dan of je bestelling recht geeft op terugbetaling en hoeveel tijd je hebt.' },
      { q: 'Mag ik de terugbetaling eisen?', a: 'Ja, dat is een recht. De vordering volgt een precieze procedure: Archer geeft ze je, afgestemd op je bestelling en je aankoopbewijs.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Afzien kan dan de juiste rekening zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou opeist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je dossier heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn bestelling analyseren · Gratis',
    proof: {
      kicker: '● TERUGBETALING VERSCHULDIGD',
      headline: 'De verkoper zit fout, niet jij.',
      summary: 'Je bestelling moest binnen 30 dagen geleverd worden: er zijn 52 dagen verstreken zonder nieuws. Het recht op ontbinding van het contract staat open, met integrale terugbetaling.',
      kpis: [
        { label: 'Betaald bedrag', sub: 'niet-geleverde bestelling' },
        { label: 'Wat je recupereert', sub: 'integrale terugbetaling' },
        { label: 'Termijn om te handelen', value: 'Onmiddellijk', sub: 'wettelijke termijn overschreden' },
      ],
      args: [
        { title: 'Levering niet uitgevoerd na 52 dagen', basis: 'Art. VI.43 WER · leveringsplicht binnen 30 dagen', tag: 'STERK' },
        { title: 'Geen antwoord van de verkoper op de herinneringen', basis: 'Art. VI.44 WER · recht op ontbinding van het contract', tag: 'STERK' },
        { title: 'Geen verifieerbare wettelijke vermeldingen op de site', basis: 'Art. VI.45 WER · informatieplicht', tag: 'MATIG' },
      ],
      letter: [
        'De ingebrekestelling om binnen 8 dagen te leveren',
        'De vraag tot ontbinding en integrale terugbetaling',
        'De verwijzing naar de toepasselijke artikelen van het WER',
        'De procedure van bankbetwisting bij stilzwijgen',
      ],
      cta: 'Mijn bestelling analyseren · Gratis',
      projection: [
        { label: 'Terugbetaling verkregen' },
        { label: 'Gedeeltelijke terugbetaling' },
        { label: 'Zonder antwoord' },
      ],
      note: 'En als je bestelling nog binnen de termijn valt, zegt Archer het je, geen nutteloze stap.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'abonnement-piege': {
    badge: '🔁 Abonnementsval',
    h1: ['Een gratis proefperiode die je factureert?', 'Misschien heb je nooit toegestemd.'],
    dropzone: 'Sleep je uittreksel of de e-mail hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je abonnement',
    checksIntro: 'Eén van deze punten kan volstaan om je afhoudingen terug te krijgen',
    checks: [
      ['Je uitdrukkelijke toestemming tot betaling', '· een gratis proefperiode geldt niet als aankoopakkoord'],
      ['De voorafgaande informatie over de verlenging', '· verplicht, en vaak afwezig'],
      ['De werkelijke eenvoud van opzegging', '· een moeilijk gemaakte opzegging is betwistbaar'],
      ['De verplichte vermeldingen van het verlengingsbeding', '· vetgedrukt, in een apart kader, op de eerste pagina'],
      ['De al dan niet aanvaarde algemene voorwaarden', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op het Belgisch Wetboek van economisch recht. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je abonnement zoals zij.',
    bandLead: 'De AI analyseert je dossier in 7 passes.',
    faqTitle: 'Jouw vragen over abonnementsvallen',
    faqs: [
      { q: 'Kan ik nu opzeggen?', a: 'Ja. En als je uitdrukkelijke toestemming tot betaling niet aantoonbaar is, kan je ook de reeds afgehouden bedragen opeisen. Scan eerst: je weet dan of je abonnement geldig tot stand kwam en hoeveel tijd je hebt.' },
      { q: 'Mag ik opeisen wat werd afgehouden?', a: 'Ja, dat is een recht. De vordering volgt een precieze procedure: Archer geeft ze je, afgestemd op je abonnement en je afhoudingen.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Afzien kan dan de juiste rekening zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou opeist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je afhoudingen lopen door.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn abonnement analyseren · Gratis',
    proof: {
      kicker: '● AFHOUDING BETWISTBAAR',
      headline: 'Dit heb je nooit aanvaard.',
      summary: 'Een gratis proefperiode werd een betalend abonnement zonder dat je uitdrukkelijke toestemming tot betaling kan worden aangetoond, en de voorafgaande informatie over de verlenging duikt nergens op.',
      kpis: [
        { label: 'Elke maand afgehouden', sub: 'sinds 4 maanden' },
        { label: 'Recupereerbaar', sub: 'totaal afgehouden' },
        { label: 'Na te leven termijn', value: '8 weken', sub: 'per SEPA-domiciliëring' },
      ],
      args: [
        { title: 'Geen uitdrukkelijke toestemming tot betaling na de proefperiode', basis: 'Art. VI.45 WER · verplichte uitdrukkelijke instemming met de betalingsverplichting', tag: 'STERK' },
        { title: 'Voorafgaande informatie over de verlenging ontbreekt', basis: 'Boek VI WER · oneerlijke handelspraktijken', tag: 'STERK' },
        { title: 'Opzeggingsprocedure moeilijk toegankelijk gemaakt', basis: 'Boek VI WER · onrechtmatige bedingen en praktijken', tag: 'MATIG' },
      ],
      letter: [
        'De onmiddellijke en kosteloze opzegging van het abonnement',
        'De vraag tot terugbetaling van de afgehouden bedragen',
        'De vraag naar het bewijs van je toestemming tot betaling',
        'De herroeping van het domiciliëringsmandaat bij je bank',
      ],
      cta: 'Mijn abonnement analyseren · Gratis',
      projection: [
        { label: 'Terugbetaling verkregen' },
        { label: 'Opzegging + gebaar' },
        { label: 'Enkel opzegging' },
      ],
      note: 'En als je wel degelijk hebt aanvaard, zegt Archer het je, en legt het uit hoe je netjes opzegt, zonder kosten.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },

  'sinistre-refuse': {
    badge: '🛡️ Schadegeval geweigerd',
    h1: ['Geweigerd met een standaardbrief?', 'Laat hun weigering nakijken.'],
    dropzone: 'Sleep de weigeringsbrief hierheen, of klik',
    checksTitle: 'Wat Archer nakijkt op je weigeringsbrief',
    checksIntro: 'Eén van deze gebreken kan volstaan om de weigering te doen vallen',
    checks: [
      ['De motivering van de weigering', '· moet precies en gedocumenteerd zijn, geen geciteerd beding'],
      ['Het ingeroepen uitsluitingsbeding', '· de geldigheid en de werkelijke toepassing op jouw geval'],
      ['Het bewijs dat de verzekeraar aanbrengt', '· hij moet de uitsluiting bewijzen, niet jij'],
      ['De verplichte vermeldingen van de polis', '· de uitsluitingen moeten er limitatief in opgesomd staan'],
      ['Je recht op een tegenexpertise', ''],
      ['Het gevorderde bedrag', 'tegenover de officiële tarieven'],
    ],
    legalNote: 'Elke controle steunt op de Belgische verzekeringswet. Elke geciteerde referentie is geverifieerd.',
    aiTail: 'Geen enkele generieke chatbot leest je weigeringsbrief zoals zij.',
    bandLead: 'De AI analyseert je dossier in 7 passes.',
    faqTitle: 'Jouw vragen over weigeringen door de verzekering',
    faqs: [
      { q: 'Mogen ze weigeren?', a: 'Alleen op grond van een in het contract voorziene uitsluiting, en het is aan de verzekeraar om die te bewijzen, niet aan jou. Scan eerst: je weet dan of de weigeringsbrief betwistbaar is en hoeveel tijd je hebt.' },
      { q: 'Mag ik het wettelijk betwisten?', a: 'Ja, dat is een recht. De betwisting volgt een precieze procedure: Archer geeft ze je, afgestemd op je weigeringsbrief.' },
      { q: 'En als het dossier van de tegenpartij stevig is?', a: 'Archer zegt het je onomwonden. Afzien kan dan de juiste rekening zijn, en je hebt geen advocaat betaald om dat te vernemen.' },
      { q: 'Hoeveel kost het?', a: 'De scan en het verdict: gratis, zonder kaart. Wil je dat Archer voor jou betwist: vanaf 34€/maand met 12 maanden vast, 39€ zonder verplichting.' },
    ],
    finalCta: ['Je beroep heeft termijnen.', 'Elke dag telt.'],
    finalCtaBtn: 'Mijn weigering analyseren · Gratis',
    proof: {
      kicker: '● WEIGERING BETWISTBAAR',
      headline: 'Het ingeroepen beding is niet van toepassing.',
      summary: 'De verzekeraar weigert je schadegeval met een beroep op een uitsluiting wegens gebrek aan onderhoud, maar geen enkel expertiseverslag staaft dat. Deze weigering steunt op een bewering, niet op een bewijs.',
      kpis: [
        { label: 'Bedrag van het schadegeval', sub: 'geraamde waarde' },
        { label: 'Wat je recupereert', sub: 'als het lukt' },
        { label: 'Termijn om te betwisten', value: '1 maand', sub: 'vóór verjaring' },
      ],
      args: [
        { title: 'Weigering niet gemotiveerd door een gedocumenteerde expertise', basis: 'Wet van 4 april 2014, art. 62 · motiveringsplicht', tag: 'STERK' },
        { title: 'Geen bewijs van het ingeroepen gebrek', basis: 'Art. 1315 oud Burgerlijk Wetboek · de bewijslast rust op de verzekeraar', tag: 'STERK' },
        { title: 'Behandelingstermijn van het dossier overschreden', basis: 'Art. 22 Wet van 4 april 2014 · redelijke termijn', tag: 'MATIG' },
      ],
      letter: [
        'De betwisting van de weigering en de vraag naar het expertiseverslag',
        'De vraag naar een onafhankelijke tegenexpertise',
        'De ingebrekestelling om het dossier opnieuw te onderzoeken',
        'De herinnering aan de beroepswegen (ombudsman van de verzekeringen)',
      ],
      cta: 'Mijn weigering analyseren · Gratis',
      projection: [
        { label: 'Vergoeding verkregen' },
        { label: 'Gedeeltelijke vergoeding' },
        { label: 'Weigering behouden' },
      ],
      note: 'En als de weigering gegrond is, legt Archer het je duidelijk uit, zodat je weet waar je aan toe bent.',
      foot: ['ANALYSE GEGENEREERD IN 58 SECONDEN', '4 GEVERIFIEERDE WETTELIJKE REFERENTIES · BELGISCH RECHT'],
      juristPrice: 'Vanaf 34 €/maand',
    },
  },
};

export default LANDING_THEMES_NL;
